import copy from '../copy/en/additional-office.json' with { type: 'json' };
import type { NativeSignerPayload } from './native-intake.js';
import { createOfficeContext } from './office-context.js';

const normalized = (value: unknown) => String(value ?? '').normalize('NFKC').toLocaleLowerCase('en-US').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
/** Conservative, local duplicate warning; not an address verification service. */
export function officeAddressKey(payload: NativeSignerPayload): string {
  const profile = payload.intakeProfile as { answers?: Record<string, unknown> } | undefined;
  const answers = profile?.answers ?? {};
  const parts = [
    [answers['locations.addressLine1'], answers['locations.addressLine2']].filter(Boolean).join(' '),
    answers['locations.city'], answers['locations.region'], String(answers['locations.postalCode'] ?? '').slice(0, 5),
    answers['locations.countryCode'] || 'US',
  ].map(normalized);
  return parts.every(Boolean) ? JSON.stringify(parts) : '';
}
export function officeSignerKey(payload: NativeSignerPayload): string {
  return JSON.stringify([payload.signerName.trim().toLocaleLowerCase('en-US'), payload.email.trim().toLocaleLowerCase('en-US')]);
}

interface Options {
  form: HTMLFormElement;
  onStart(payload: NativeSignerPayload): Promise<boolean>;
  onReview(): void;
  onEditLocation(): void;
  preview?: boolean;
}

/** In-tab convenience only: no signature, consent, credentials or history in storage. */
export function createAdditionalOfficeFlow({ form, onStart, onReview, onEditLocation, preview = false }: Options) {
  const doc = form.ownerDocument;
  const button = doc.querySelector<HTMLButtonElement>('#addOffice')!;
  const completionHint = doc.querySelector<HTMLElement>('#addOfficeHint')!;
  button.textContent = copy.addOffice;
  completionHint.textContent = copy.completionHint;
  const panel = doc.querySelector<HTMLElement>('#sessionPanel')!;
  const notice = doc.createElement('div'); notice.className = 'additional-office-notice hidden';
  const title = doc.createElement('strong'); title.textContent = copy.editingTitle;
  const hint = doc.createElement('p'); hint.className = 'hint'; hint.textContent = preview ? copy.previewHint : copy.editingHint;
  const replaceHint = doc.createElement('p'); replaceHint.className = 'hint'; replaceHint.textContent = copy.replaceHint;
  const review = doc.createElement('button'); review.type = 'button'; review.className = 'btn btn-ghost btn-small'; review.textContent = copy.reviewOffice;
  notice.append(title, hint, replaceHint, review); panel.append(notice);
  const history = doc.createElement('details'); history.className = 'completed-office-files hidden';
  const historyTitle = doc.createElement('summary'); historyTitle.textContent = copy.previousDocuments;
  const files = doc.createElement('ul'); history.append(historyTitle, files); panel.append(history);
  const warning = doc.createElement('div'); warning.id = 'duplicateOfficeNotice'; warning.className = 'duplicate-office-notice hidden'; warning.setAttribute('role', 'note');
  const warningTitle = doc.createElement('strong'); warningTitle.textContent = copy.duplicateTitle;
  const warningHint = doc.createElement('p'); warningHint.className = 'hint'; warningHint.textContent = copy.duplicateHint;
  const confirmLabel = doc.createElement('label'); confirmLabel.className = 'consent';
  const confirm = doc.createElement('input'); confirm.type = 'checkbox'; confirm.id = 'confirmRepeatedOffice';
  const confirmText = doc.createElement('span'); confirmText.textContent = copy.duplicateConfirm;
  confirmLabel.append(confirm, confirmText); warning.append(warningTitle, warningHint, confirmLabel);
  form.querySelector('[data-data-stage="0"] .stage-header')!.after(warning);
  let latest: NativeSignerPayload | null = null;
  let active = false;
  let pending = false;
  let busy = false;
  let disposed = false;
  let currentAddress = '';
  const addresses = new Set<string>();
  const downloads = new Set<string>();
  let currentPayload: NativeSignerPayload | null = null;
  let contextVisible = true;
  const context = createOfficeContext({
    surface: panel.closest<HTMLElement>('.form-surface') ?? panel.parentElement!,
    onEditLocation: () => { if (!busy && !pending && !disposed) onEditLocation(); },
    onReviewMatch: () => {
      if (busy || pending || disposed || !currentAddress || !addresses.has(currentAddress)) return;
      onEditLocation();
      confirm.focus(); warning.scrollIntoView({ block: 'center' });
    },
  });
  function renderContext() {
    const answers = (currentPayload?.intakeProfile as { answers?: Record<string, unknown> })?.answers ?? {};
    const address = ['locations.addressLine1', 'locations.addressLine2', 'locations.city', 'locations.region', 'locations.postalCode']
      .map(key => String(answers[key] ?? '').trim()).filter(Boolean).join(', ');
    context.update({
      practiceName: String(currentPayload?.practiceName ?? '').trim(), address,
      repeated: Boolean(currentAddress && addresses.has(currentAddress)), acknowledged: confirm.checked,
      preview, visible: contextVisible && currentPayload !== null, disabled: busy || pending,
    });
  }
  confirm.addEventListener('change', renderContext);
  const begin = async () => {
    if (!latest || busy || pending || disposed) return;
    pending = true; button.disabled = true; renderContext();
    const wasActive = active;
    active = true;
    try {
      const ok = await onStart(JSON.parse(JSON.stringify(latest)) as NativeSignerPayload);
      if (disposed) return;
      if (!ok) { active = wasActive; return; }
      confirm.checked = false; currentAddress = ''; contextVisible = true;
      notice.classList.remove('hidden');
      history.classList.toggle('hidden', !files.childElementCount);
      changed(latest);
    } finally { pending = false; button.disabled = busy; renderContext(); }
  };
  const requestReview = () => { if (active && !busy && !pending) onReview(); };
  button.addEventListener('click', begin);
  review.addEventListener('click', requestReview);
  function changed(payload: NativeSignerPayload) {
    currentPayload = payload;
    const key = officeAddressKey(payload);
    if (key !== currentAddress) confirm.checked = false;
    currentAddress = key;
    warning.classList.toggle('hidden', !contextVisible || !key || !addresses.has(key));
    renderContext();
  }
  return {
    get active() { return active; },
    completed(payload: NativeSignerPayload | null, url: string, filename: string, eligible: boolean) {
      latest = eligible && payload ? JSON.parse(JSON.stringify(payload)) as NativeSignerPayload : null;
      button.classList.toggle('hidden', !latest); completionHint.classList.toggle('hidden', !latest);
      notice.classList.add('hidden'); warning.classList.add('hidden');
      contextVisible = false; renderContext();
      if (!latest) return;
      const key = officeAddressKey(latest); if (key) addresses.add(key);
      if (url && !downloads.has(url)) {
        downloads.add(url);
        const row = doc.createElement('li');
        const link = doc.createElement('a'); link.href = url; link.download = filename;
        const answers = (latest.intakeProfile as { answers?: Record<string, unknown> })?.answers ?? {};
        link.textContent = [latest.practiceName, answers['locations.addressLine1'], answers['locations.city']].filter(Boolean).join(' — ');
        row.append(link); files.append(row);
      }
    },
    changed,
    setVisible(value: boolean) { contextVisible = value; renderContext(); },
    acceptsAddress(payload: NativeSignerPayload) {
      changed(payload);
      return !active || !currentAddress || !addresses.has(currentAddress) || confirm.checked;
    },
    focusWarning() { confirm.focus(); warning.scrollIntoView({ block: 'center' }); },
    setBusy(value: boolean) { busy = value; button.disabled = value || pending; review.disabled = value; confirm.disabled = value; renderContext(); },
    dispose() { disposed = true; button.removeEventListener('click', begin); review.removeEventListener('click', requestReview); confirm.removeEventListener('change', renderContext); context.dispose(); notice.remove(); history.remove(); warning.remove(); },
  };
}
